
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
 
// ===== Scroll in (fade/slide) animation logic =====
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}
 
function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;
 
  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }
 
  // Respect the OS-level motion preference: skip hiding elements pending
  // scroll entirely rather than revealing them without a transition.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
 
  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}
 
// ===== Zoom in animation logic =====
// Shared across every '.animate--zoom-in' element on the page: one
// IntersectionObserver and one scroll listener instead of one pair per
// element. On a page with several zoom-in images the previous version
// registered a separate global scroll listener for each one; this keeps
// a single rAF-batched update loop that only touches elements currently
// in view.
const ZOOM_SCALE_AMOUNT = 0.2 / 100;
const zoomVisibleElements = new Set();
const zoomRegisteredElements = new WeakSet();
let zoomObserver = null;
let zoomScrollTicking = false;
 
function updateZoomRatio(element) {
  element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_SCALE_AMOUNT * percentageSeen(element));
}
 
function updateVisibleZoomElements() {
  zoomVisibleElements.forEach(updateZoomRatio);
  zoomScrollTicking = false;
}
 
function ensureZoomScrollListener() {
  if (ensureZoomScrollListener.attached) return;
  ensureZoomScrollListener.attached = true;
  window.addEventListener(
    'scroll',
    () => {
      if (zoomVisibleElements.size === 0 || zoomScrollTicking) return;
      zoomScrollTicking = true;
      requestAnimationFrame(updateVisibleZoomElements);
    },
    { passive: true }
  );
}
 
function ensureZoomObserver() {
  if (zoomObserver) return zoomObserver;
  zoomObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        zoomVisibleElements.add(entry.target);
      } else {
        zoomVisibleElements.delete(entry.target);
      }
    });
  });
  return zoomObserver;
}
 
function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
 
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;
 
  const observer = ensureZoomObserver();
 
  animationTriggerElements.forEach((element) => {
    if (zoomRegisteredElements.has(element)) return; // already wired up, e.g. re-fired on section reload
    zoomRegisteredElements.add(element);
    observer.observe(element);
    updateZoomRatio(element);
  });
 
  ensureZoomScrollListener();
}
 
function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;
 
  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }
 
  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}
 
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});
 
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    // Also wires up zoom-in for blocks/sections added live in the editor,
    // which the original version never did (they only got the effect
    // after a full page reload).
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
 